import { Divider, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { startCase } from 'lodash';

import { Alert, Text, api, getQueryProps, isDefined, isTruthy } from 'common';

import { EndpointCard } from './EndpointCard/EndpointCard';
import { useFlowDefinitionContext } from '../../../../context';
import { BlockAutocomplete, BlockFreeSoloMultiAutocomplete } from '../../ConfigFields/Autocomplete';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockKeyValuesField } from '../../ConfigFields/KeyValues/KeyValuesField';
import { BlockRadioGroupField } from '../../ConfigFields/RadioGroupField';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

type AdvancedFhirBlockConfig = api.FlowDefinition.AdvancedFhirBlock['config'];

const RequestBodyFormat = api.FlowDefinition.RequestBodyFormat;

interface AdvancedFhirFormFieldsProps {
  fieldNamePrefix?: string;
}

const bodyFormatOptions = [
  { label: 'None', value: RequestBodyFormat.None },
  { label: 'JSON', value: RequestBodyFormat.RawJson },
  { label: 'Form URL Encoded', value: RequestBodyFormat.FormUrlEncoded },
];

export function AdvancedFhirFormFields(props: AdvancedFhirFormFieldsProps) {
  const { flowDefinition } = useFlowDefinitionContext();
  const { fieldNamePrefix = 'config' } = props;

  const { value: requestBodyFormatValue } = useSelectedNodeField<
    AdvancedFhirBlockConfig['bodyFormat']
  >(`${fieldNamePrefix}.bodyFormat`);
  const { value: selectedApiInstanceId } = useSelectedNodeField<
    AdvancedFhirBlockConfig['apiInstanceId']
  >(`${fieldNamePrefix}.apiInstanceId`);
  const { value: customEndpointMethod, setValue: setCustomEndpointMethod } = useSelectedNodeField<
    AdvancedFhirBlockConfig['method']
  >(`${fieldNamePrefix}.method`);
  const { value: path, setValue: setPath } = useSelectedNodeField<AdvancedFhirBlockConfig['path']>(
    `${fieldNamePrefix}.path`
  );
  const { value: params } = useSelectedNodeField<AdvancedFhirBlockConfig['params']>(
    `${fieldNamePrefix}.params`
  );
  const { value: apiInstanceFhirEndpointId, setValue: setApiInstanceFhirEndpointId } =
    useSelectedNodeField<AdvancedFhirBlockConfig['apiInstanceFhirEndpointId']>(
      `${fieldNamePrefix}.apiInstanceFhirEndpointId`
    );

  const { data: apiInstances = [] } = useQuery({
    ...getQueryProps(api.client.Orchestrator.ApiInstance, 'findAllBasic', {
      organizationId: flowDefinition.organizationId,
    }),
  });

  const selectedApiInstance = apiInstances.find(
    (apiInstance) => apiInstance.id === selectedApiInstanceId
  );

  const isEpicApiInstance = selectedApiInstance?.kind === api.Orchestrator.ApiInstance.Kind.EPIC;
  const isCernerApiInstance =
    selectedApiInstance?.kind === api.Orchestrator.ApiInstance.Kind.FHIRCerner;

  const { data: fhirEndpoints = [] } = useQuery({
    ...getQueryProps(
      api.client.Orchestrator.ApiInstance,
      'findFhirEndpoints',
      selectedApiInstanceId ?? 'INVALID_API_INSTANCE_ID',
      {}
    ),
    enabled: isEpicApiInstance || isCernerApiInstance,
  });

  const isPatientSearchPath =
    customEndpointMethod === api.Orchestrator.ApiInstanceFhir.ApiInstanceFhirMethod.GET &&
    isDefined(path) &&
    // Matches paths that are Patient searches (not specific patient reads)
    // Examples: "Patient", "/Patient", "Patient?name=John", "/Patient?identifier=123", "/Patient/"
    // Does NOT match: "Patient/123", "/Patient/abc-def", "patient/123?_include=*"
    /^\/?Patient\/?(?:[?]|$)/i.test(path.trim());
  const isIdentifierInSearchParams =
    isDefined(params) && params.some((param) => param.key === 'identifier');

  const doesMethodSupportBody =
    isDefined(customEndpointMethod) &&
    [
      api.Orchestrator.ApiInstanceFhir.ApiInstanceFhirMethod.POST,
      api.Orchestrator.ApiInstanceFhir.ApiInstanceFhirMethod.PUT,
      api.Orchestrator.ApiInstanceFhir.ApiInstanceFhirMethod.PATCH,
    ].includes(customEndpointMethod);

  const resetEndpointConfiguration = () => {
    setApiInstanceFhirEndpointId(undefined);
    setCustomEndpointMethod(api.Orchestrator.ApiInstanceFhir.ApiInstanceFhirMethod.GET);
    setPath('/');
  };

  return (
    <Stack gap={2}>
      <BlockSelectField
        label="API Instance"
        fieldName="config.apiInstanceId"
        fieldVariant="SelectOrBlockValueTemplate"
        required={true}
        options={apiInstances.map((apiInstance) => ({
          label: apiInstance.name,
          value: apiInstance.id,
        }))}
        onChange={(_event, newValue) => {
          const selectedApiInstance = apiInstances.find(
            (apiInstance) => apiInstance.id === newValue
          );

          if (selectedApiInstance?.kind !== api.Orchestrator.ApiInstance.Kind.EPIC) {
            setApiInstanceFhirEndpointId(undefined);
          }
        }}
        onClear={() => {
          setApiInstanceFhirEndpointId(undefined);
        }}
        helperText={
          apiInstances.length === 0
            ? 'No API instances available for this organization. Please create one first.'
            : `Select the API instance to use for FHIR requests. Only API instances from this flow's organization are shown.`
        }
      />
      {(isEpicApiInstance || isCernerApiInstance) && (
        <>
          <Divider />
          <Text variant="heading6">Endpoint</Text>
          <Text variant="body2">
            Select an endpoint to automatically fill its method and path, or add manually to create
            a custom endpoint.
          </Text>
        </>
      )}
      {isTruthy(selectedApiInstanceId) && (
        <>
          {(isEpicApiInstance || isCernerApiInstance) && (
            <BlockAutocomplete
              fieldName={`${fieldNamePrefix}.apiInstanceFhirEndpointId`}
              label="Select an Endpoint"
              required
              options={fhirEndpoints.map((fhirEndpoint) => fhirEndpoint.id) || []}
              getOptionLabel={(fhirEndpointId) =>
                fhirEndpoints.find((fhirEndpoint) => fhirEndpoint.id === fhirEndpointId)?.name ||
                'Search'
              }
              onChange={(newValue) => {
                const selectedFhirEndpoint = fhirEndpoints.find(
                  (fhirEndpoint) => fhirEndpoint.id === newValue
                );

                if (isDefined(selectedFhirEndpoint)) {
                  setPath(selectedFhirEndpoint.path);
                  setCustomEndpointMethod(selectedFhirEndpoint.method);
                } else {
                  resetEndpointConfiguration();
                }
              }}
            />
          )}
          <BlockSelectField
            fieldName={`${fieldNamePrefix}.method`}
            label="Method"
            options={Object.values(api.Orchestrator.ApiInstanceFhir.ApiInstanceFhirMethod).map(
              (method) => ({
                label: method,
                value: method,
              })
            )}
            required
            disabled={isDefined(apiInstanceFhirEndpointId)}
          />
          <BlockValueTemplateField
            fieldName={`${fieldNamePrefix}.path`}
            label="Path"
            required
            helperText="Enter the path"
          />
          {isDefined(apiInstanceFhirEndpointId) && (
            <EndpointCard
              endpointId={apiInstanceFhirEndpointId}
              onDelete={() => {
                setApiInstanceFhirEndpointId(undefined);
                resetEndpointConfiguration();
              }}
            />
          )}
          {isPatientSearchPath && !isIdentifierInSearchParams && (
            <Alert variant="warning" title="Patient Lookup">
              Use the patient <code>identifier</code> parameter to look up patients (e.g.,{' '}
              <code>
                Patient?identifier={'<'}system{'>'}|{'<'}ehrPatientId{'>'}
              </code>
              ). In addition, you can search using patient demographics such as first name, last
              name, and date of birth (e.g.,{' '}
              <code>
                Patient?identifier={'<'}system{'>'}|{'<'}ehrPatientId{'>'}&given={'<'}firstName{'>'}
                &family={'<'}lastName{'>'}&birthdate={'<'}
                YYYY-MM-DD{'>'}
              </code>
              ).
            </Alert>
          )}
        </>
      )}
      <Divider />
      <Stack gap={2}>
        <BlockKeyValuesField
          fieldName={`${fieldNamePrefix}.params`}
          addRowHelperText="parameter"
          label="Parameters"
          helperText="Enter API Request Parameters."
        />
        <Divider />
        <BlockKeyValuesField
          fieldName={`${fieldNamePrefix}.headers`}
          addRowHelperText="header"
          label="Headers"
          helperText="Enter API Request Headers."
        />
        <BlockFreeSoloMultiAutocomplete
          fieldName={`${fieldNamePrefix}.selectedResponseHeaders`}
          label="Response Headers"
          helperText="Enter response headers to be returned."
        />
        <BlockSelectField
          fieldName={`${fieldNamePrefix}.responseBodyFormat`}
          label="Response Body Format"
          helperText="Auto Detect parses JSON when possible. Parsed Base64 returns binary payloads (e.g. Epic Binary PDFs) as a base64 string."
          options={Object.values(api.FlowDefinition.ResponseBodyFormat).map((format) => ({
            label: startCase(format),
            value: format,
          }))}
          defaultValue={api.FlowDefinition.ResponseBodyFormat.AutoDetect}
        />
        {doesMethodSupportBody && (
          <>
            <Divider />
            <BlockRadioGroupField
              options={bodyFormatOptions}
              label="Request Body"
              fieldName={`${fieldNamePrefix}.bodyFormat`}
              defaultValue={RequestBodyFormat.RawJson}
              row
            />
            {requestBodyFormatValue !== RequestBodyFormat.None && (
              <BlockValueTemplateFieldArea
                fieldName={`${fieldNamePrefix}.body`}
                label="Body"
                helperText="Enter API Request Body."
              />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
}
